import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { Chart, ChartConfiguration } from 'chart.js';
import { colorAsARGB, MODEL_COLOURS } from "../../model-services/model-item-enums";
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels'
import { formatNumber } from "@angular/common";
import { DEFAULT_CURRENCY, getCurrency } from "../../common/organisation";
import { OrganisationService } from "../../organisation/organisation.service";

const MAX_ITEMS = 35

// the chart's text and lines, in the app's palette
export const INK = '#1E2233'
export const SLATE = '#5b6172'
export const GRID = '#eef0f4'
export const FONT_FAMILY = "'Montserrat', 'Roboto', system-ui, sans-serif"
// a name beside the bars, shortened to the room the chart gives it (about half its width, less the room kept for the values)
export const fitLabel = (chart:any, label:string) => {
    const ctx:CanvasRenderingContext2D | undefined = chart?.ctx
    if (!ctx || !label) return label
    const room = Math.max(60, (chart.width - 48) / 2 - 14)
    ctx.save()
    ctx.font = `12px ${FONT_FAMILY}`
    let text = label
    if (ctx.measureText(text).width > room) {
        while (text.length > 1 && ctx.measureText(text + '…').width > room) text = text.slice(0, -1)
        text = text.trimEnd() + '…'
    }
    ctx.restore()
    return text
}

export class BarData {
    constructor(public _key:string, public name:string, public values:number[], public colors:string[], public hoverColors:string[], public csValues?:number[], public csColors?:string[], public csHoverColors?:string[]) {
    }

    getTotalValue() {
        return this.values.reduce((t, v) => t+=v, 0)
    }
}

// a number shortened for a label or an axis: 1,234 → 1.2K, 3,400,000 → 3.4M
export const abbreviate = (value:number, locale:string) => {
    const v = Math.abs(value)
    const sign = value < 0 ? '-' : ''
    if (v >= 1e9) return sign + formatNumber(v/1e9, locale, '1.0-1') + 'B'
    if (v >= 1e6) return sign + formatNumber(v/1e6, locale, '1.0-1') + 'M'
    if (v >= 1e4) return sign + formatNumber(v/1e3, locale, '1.0-1') + 'K'
    // small values keep their decimals, so an axis from 0 to 1 doesn't read 0, 0, 0, 1, 1
    if (v < 10) return sign + formatNumber(v, locale, '1.0-2')
    return sign + formatNumber(Math.round(v), locale, '1.0-0')
}

@Component({
    selector: 'app-bar-chart',
    standalone: false,
    templateUrl: './bar-chart.html',
    styleUrls: ['./bar-chart.css']
})
export class BarChartComponent implements OnInit, OnChanges {
    @Input() data: BarData[];
    @Input() comparison: string
    @Input() stacks: string[]
    @Input() isStacked: boolean
    @Input() indexAxis: 'x'|'y'
    @Input() width: number
    @Input() height:number
    @Input() hideXLabels: boolean = false
    @Input() hideLegend: boolean = false
    @Input() sort:boolean = true
    @Input() topNumber:number
    @Input() hideZeros:boolean

    @Input() xLabel:string
    @Input() yLabel:string
    @Input() isCurrency:boolean

    @Output() selectNodeEvent = new EventEmitter<string>();

    public chartOptions: ChartConfiguration['options'] = {}

    public chartPlugins = [ChartDataLabels];
    locale:string

    labels = []
    values = []
    nodeKeys = []

    constructor(@Inject(LOCALE_ID) locale: string, private organisationService:OrganisationService) {
        this.locale = locale
        // the plugin is given to the chart through [plugins] rather than registered with Chart: a global registration from
        // a constructor reaches every chart already on the page, which never had the plugin's init and so throw on their
        // next draw (seen as "Cannot read properties of undefined (reading '_labels')" on the Insights view's treemap)
        Chart.defaults.set('plugins.datalabels', {
            color: '#000000',
            clip: true,
            clamp: false,
            display: 'auto',
            anchor: 'end',
            align: 'start',
            formatter: this.formatNumberLabel,
            font: {
                weight: 'bold'
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["data"] || changes["topNumber"] || changes["hideZeros"] || changes["xLabel"] || changes["isCurrency"]) {
            this.populateData()
        }
    }

    ngOnInit(): void {
        this.populateData()
    }

    formatNumberLabel(value:any, context:Context) {
        return formatNumber(Math.round(value), 'en-NZ')
    }

    // a value as the chart shows it: shortened, and in the organisation's currency when it is money
    private formatValue(value:number, short:boolean) {
        if (value === null || value === undefined || isNaN(value)) return ''
        const symbol = this.isCurrency ? getCurrency(this.organisationService.organisation?.currencyCode || DEFAULT_CURRENCY).symbol : ''
        const text = short ? abbreviate(value, this.locale) : formatNumber(Math.round(value), this.locale, '1.0-0')
        return value < 0 && symbol ? '-' + symbol + text.substring(1) : symbol + text
    }

    getStyle() {
        // the chart fills its box; a size given sets the box
        let style = "position: relative;"
        style += this.height ? `height:${this.height}px;` : "height:60vh;"
        style += this.width ? `width:${this.width}px;` : "width:100%;"
        return style
    }

    // the chart's look: quiet axes and grid, rounded bars, values beside the bars, and money shown as money
    private buildOptions(): ChartConfiguration['options'] {
        const horizontal = (this.indexAxis || 'y') === 'y'
        const valueAxis = horizontal ? 'x' : 'y'
        const categoryAxis = horizontal ? 'y' : 'x'
        const stacked = !!this.isStacked
        const scales:any = {}
        scales[valueAxis] = {
            display: !(this.hideXLabels && valueAxis === 'x'),
            stacked,
            beginAtZero: true,
            grid: { color: GRID, drawTicks: false },
            border: { display: false },
            ticks: { color: SLATE, padding: 6, font: { family: FONT_FAMILY, size: 11 }, callback: (v:any) => this.formatValue(Number(v), true) },
            title: this.xLabel ? { display: true, text: this.xLabel, color: SLATE, font: { family: FONT_FAMILY, size: 12, weight: 600 }, padding: { top: 8 } } : { display: false },
        }
        scales[categoryAxis] = {
            display: !(this.hideXLabels && categoryAxis === 'x'),
            stacked,
            grid: { display: false },
            border: { color: '#d5d9e2' },
            ticks: {
                color: INK, autoSkip: !horizontal, padding: 6, font: { family: FONT_FAMILY, size: 12 },
                // a long name is shortened beside the bars, which have only so much room; hovering shows it in full
                callback: function(this:any, v:any) {
                    const label = String(this.getLabelForValue(Number(v)) ?? '')
                    return horizontal ? fitLabel(this.chart, label) : label
                },
            },
        }
        return <any>{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: horizontal ? 'y' : 'x',
            // room for the value labels at the end of the bars
            layout: { padding: horizontal ? { right: 48 } : { top: 20 } },
            datasets: { bar: { borderRadius: 4, borderSkipped: false, maxBarThickness: 26, categoryPercentage: 0.8, barPercentage: 0.9 } },
            scales,
            plugins: {
                legend: {
                    display: !this.hideLegend,
                    position: 'top',
                    align: 'end',
                    labels: { color: SLATE, usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 10, boxHeight: 10, padding: 14, font: { family: FONT_FAMILY, size: 12 } },
                },
                tooltip: {
                    backgroundColor: INK,
                    padding: 10,
                    cornerRadius: 6,
                    titleFont: { family: FONT_FAMILY, size: 12, weight: 600 },
                    bodyFont: { family: FONT_FAMILY, size: 12 },
                    callbacks: { label: (c:any) => `${c.dataset.label ? c.dataset.label + ': ' : ''}${this.formatValue(c.parsed[valueAxis], false)}` },
                },
                // the value just past the end of each bar; a stacked bar shows its total there, once, on its last segment
                datalabels: stacked ? {
                    color: INK, anchor: 'end', align: 'end', offset: 4, clamp: true, clip: false,
                    font: { family: FONT_FAMILY, size: 11, weight: 600 },
                    display: (c:any) => {
                        const sets = c.chart.data.datasets
                        const last = sets.map((d:any, i:number) => ({ d, i })).filter((x:any) => x.d.stack === sets[c.datasetIndex].stack).pop()
                        return last?.i === c.datasetIndex && this.stackTotal(c) !== 0
                    },
                    formatter: (v:number, c:any) => this.formatValue(this.stackTotal(c), true),
                } : {
                    color: INK, anchor: 'end', align: 'end', offset: 4, clamp: true, clip: false, display: 'auto',
                    font: { family: FONT_FAMILY, size: 11, weight: 600 },
                    formatter: (v:number) => v ? this.formatValue(v, true) : '',
                },
            } as any,
        }
    }

    // a stacked bar's total: its segments in the same stack, at the same place
    private stackTotal(c:any) {
        const sets = c.chart.data.datasets
        const stack = sets[c.datasetIndex].stack
        return sets.filter((d:any) => d.stack === stack).reduce((sum:number, d:any) => sum + (Number(d.data[c.dataIndex]) || 0), 0)
    }

    populateData() {
        this.chartOptions = this.buildOptions()
        if (!this.data || this.data.length === 0) return

        const numItems = this.topNumber || MAX_ITEMS

        const a = this.sort?this.data.filter(d => !this.hideZeros || d.getTotalValue() !== 0).sort((a,b) => b.getTotalValue() - a.getTotalValue()).slice(0, numItems):this.data

        this.nodeKeys = a.map(d => d._key)
        this.labels = a.map(d => d.name)
        this.values = []
        if (this.comparison && this.comparison.length > 0) {
            for (let i=0; i<this.stacks.length; i++) {
                const comparisonColour = MODEL_COLOURS.EXCLUDED_COLOR
                const comparisonHoverColor = MODEL_COLOURS.EXCLUDED_HOVER_COLOR

                this.values.push({label:this.comparison + " - " + this.stacks[i], stack:this.isStacked?"b":this.stacks[i] + "-b", data: a.map(d => d.csValues[i]), backgroundColor: a.map(d => (d.csColors&&this.getComparisonColour(d.csColors[i]))||comparisonColour), hoverBackgroundColor: a.map(d => (d.csHoverColors && d.csHoverColors[i])||comparisonHoverColor), borderColor: a.map(d => MODEL_COLOURS.RED)})
                this.values.push({label:this.stacks[i], stack:this.isStacked?"a":this.stacks[i] + "-a", data: a.map(d => d.values[i]), backgroundColor: a.map(d => d.colors[i]), hoverBackgroundColor: a.map(d => d.hoverColors[i])})
            }
        }else if (this.stacks) {
            for (let i=0; i<this.stacks.length; i++) {
                this.values.push({label:this.stacks[i], stack:this.isStacked?"a":this.stacks[i], data: a.map(d => d.values[i]), backgroundColor: a.map(d => d.colors[i]), hoverBackgroundColor: a.map(d => d.hoverColors[i])})
            }
        }
    }

    getComparisonColour(hexcolour:string) {
        const rgb = colorAsARGB(hexcolour)
        return `rgba(${rgb.r},${rgb.g},${rgb.b},0.4)`
    }

    hideChart() {}

    onSelect(data): void {
        if (data.active && data.active.length > 0) this.selectNodeEvent.emit(this.nodeKeys[data.active[0].index])
    }
}
